import React, { useMemo, useState } from 'react';
import { MdOutlineTrendingUp } from 'react-icons/md';
import { SectionCard, StatTile, SubHeading, Segmented } from './ui';
import ChartFrame from '../charts/ChartFrame';
import LineChart from '../charts/LineChart';
import Meter from '../charts/Meter';
import { fmtMoney, fmtNum, fmtMonth, fmtMonthShort, fmtRatioPct, fmtSigned, parseDate, pctChange, safeArray, isNum, DASH } from '../format';

const METRICS = [
  { value: 'median_listing_price_usd', label: 'Median price', format: (v) => fmtMoney(v, { compact: true }), full: (v) => fmtMoney(v) },
  { value: 'median_listing_price_per_sqft_usd', label: '$ / sq ft', format: (v) => fmtMoney(v), full: (v) => fmtMoney(v) },
  { value: 'median_days_on_market', label: 'Days on market', format: (v) => fmtNum(v), full: (v) => `${fmtNum(v)} days` },
  { value: 'active_listing_count', label: 'Active listings', format: (v) => fmtNum(v), full: (v) => fmtNum(v) },
  { value: 'pending_to_total_listing_ratio', label: 'Pending ratio', format: (v) => fmtRatioPct(v), full: (v) => fmtRatioPct(v, 1) },
  { value: 'price_reduced_to_total_listing_ratio', label: 'Price cuts', format: (v) => fmtRatioPct(v), full: (v) => fmtRatioPct(v, 1) },
];

function metricValue(record, key) {
  if (!record) return null;
  const v = record[key];
  if (isNum(v)) return v;
  // Peer benchmarks omit the ratio fields; derive them from counts when possible.
  if (key === 'pending_to_total_listing_ratio' && isNum(record.pending_listing_count) && isNum(record.total_listing_count) && record.total_listing_count > 0) {
    return record.pending_listing_count / record.total_listing_count;
  }
  if (key === 'price_reduced_to_total_listing_ratio' && isNum(record.price_reduced_count) && isNum(record.total_listing_count) && record.total_listing_count > 0) {
    return record.price_reduced_count / record.total_listing_count;
  }
  return null;
}

function toPoints(records, key) {
  return safeArray(records).map((r) => {
    const d = parseDate(r.month);
    return d ? { x: d.getTime(), y: metricValue(r, key) } : null;
  }).filter(Boolean);
}

function heatTone(ratio) {
  if (!isNum(ratio)) return 'accent';
  if (ratio >= 0.35) return 'good';
  if (ratio >= 0.15) return 'accent';
  return 'warning';
}

function softnessTone(ratio) {
  if (!isNum(ratio)) return 'accent';
  if (ratio >= 0.3) return 'critical';
  if (ratio >= 0.2) return 'warning';
  return 'accent';
}

export default function HousingMarketCard({ data, status }) {
  const [metric, setMetric] = useState(METRICS[0].value);
  const zip = data?.zip || {};
  const county = data?.county || null;
  const latest = zip.latest || null;
  const monthly = safeArray(zip.monthly);
  const peers = zip.peer_benchmark || null;
  const notes = safeArray(data?.notes);

  const yearAgo = useMemo(() => {
    if (!latest || monthly.length < 13) return null;
    const idx = monthly.findIndex((m) => m.month === latest.month);
    const base = idx >= 12 ? monthly[idx - 12] : monthly[monthly.length - 13];
    return base || null;
  }, [latest, monthly]);

  const current = METRICS.find((m) => m.value === metric) || METRICS[0];
  const series = useMemo(() => {
    const out = [];
    const zipPts = toPoints(monthly, metric);
    if (zipPts.some((p) => isNum(p.y))) out.push({ id: 'zip', label: `ZIP ${zip.postal_code || ''}`.trim(), color: 'var(--le-s1)', points: zipPts, emphasis: true });
    const peerPts = toPoints(peers?.monthly_medians, metric);
    if (peerPts.some((p) => isNum(p.y))) out.push({ id: 'peers', label: 'Peer ZIPs (median)', color: 'var(--le-s2)', points: peerPts });
    const countyPts = toPoints(county?.monthly, metric);
    if (countyPts.some((p) => isNum(p.y))) out.push({ id: 'county', label: 'County', color: 'var(--le-ctx)', points: countyPts });
    return out;
  }, [monthly, peers, county, metric, zip.postal_code]);

  const tableRows = useMemo(() => {
    const byMonth = new Map();
    const add = (records, key) => safeArray(records).forEach((r) => {
      if (!r?.month) return;
      const row = byMonth.get(r.month) || { month: r.month };
      row[key] = metricValue(r, metric);
      byMonth.set(r.month, row);
    });
    add(monthly, 'zip');
    add(peers?.monthly_medians, 'peers');
    add(county?.monthly, 'county');
    return Array.from(byMonth.values()).sort((a, b) => (a.month < b.month ? 1 : -1));
  }, [monthly, peers, county, metric]);

  const priceDelta = yearAgo && latest ? pctChange(yearAgo.median_listing_price_usd, latest.median_listing_price_usd) : null;
  const domDelta = yearAgo && latest && isNum(yearAgo.median_days_on_market) && isNum(latest.median_days_on_market) ? latest.median_days_on_market - yearAgo.median_days_on_market : null;

  const subtitle = data
    ? `ZIP ${zip.postal_code || DASH} · ${fmtNum(zip.months_returned)} months through ${fmtMonth(zip.period_end)}${county ? ` · county ${county.county_fips}` : ''}`
    : null;

  return (
    <SectionCard id="le-section-housing" icon={<MdOutlineTrendingUp size={20} />} title="Housing market" subtitle={subtitle} status={status} dataset={data}>
      {data ? (
        <>
          {latest ? (
            <>
              <div className="le-stat-grid">
                <StatTile label={`Median list price · ${fmtMonthShort(latest.month)}`} value={fmtMoney(latest.median_listing_price_usd)} delta={isNum(priceDelta) ? { label: `${fmtSigned(priceDelta, 1, '%')} vs 12 mo ago`, tone: priceDelta > 1 ? 'up' : priceDelta < -1 ? 'down' : 'flat' } : null} emphasis />
                <StatTile label="Median $ / sq ft" value={fmtMoney(latest.median_listing_price_per_sqft_usd)} sub={isNum(latest.median_listing_sqft) ? `Median listing ${fmtNum(latest.median_listing_sqft)} sq ft` : null} />
                <StatTile label="Median days on market" value={isNum(latest.median_days_on_market) ? `${fmtNum(latest.median_days_on_market)} days` : DASH} delta={isNum(domDelta) ? { label: `${fmtSigned(domDelta, 0, ' days')} vs 12 mo ago`, tone: domDelta > 2 ? 'down' : domDelta < -2 ? 'up' : 'flat' } : null} />
                <StatTile label="Inventory" value={`${fmtNum(latest.active_listing_count)} active`} sub={`${fmtNum(latest.pending_listing_count)} pending · ${fmtNum(latest.total_listing_count)} total`} />
                <div className="le-stat le-stat-meter">
                  <div className="le-stat-label">Demand heat · pending ÷ total</div>
                  <Meter value={latest.pending_to_total_listing_ratio} max={0.6} tone={heatTone(latest.pending_to_total_listing_ratio)} label="Pending to total listing ratio" valueLabel={fmtRatioPct(latest.pending_to_total_listing_ratio)} />
                  <div className="le-stat-sub">Higher = listings go pending faster</div>
                </div>
                <div className="le-stat le-stat-meter">
                  <div className="le-stat-label">Softness · price cuts ÷ total</div>
                  <Meter value={latest.price_reduced_to_total_listing_ratio} max={0.5} tone={softnessTone(latest.price_reduced_to_total_listing_ratio)} label="Price reduced to total listing ratio" valueLabel={fmtRatioPct(latest.price_reduced_to_total_listing_ratio)} />
                  <div className="le-stat-sub">{fmtNum(latest.price_reduced_count)} reductions · {fmtNum(latest.price_increased_count)} increases</div>
                </div>
              </div>
            </>
          ) : null}

          <SubHeading right={<Segmented options={METRICS.map((m) => ({ value: m.value, label: m.label }))} value={metric} onChange={setMetric} ariaLabel="Housing metric" />}>
            Trend · {current.label}
          </SubHeading>
          <ChartFrame
            legend={series.map((s) => ({ id: s.id, label: s.label, color: s.color, kind: 'line' }))}
            table={{
              columns: [
                { key: 'month', label: 'Month', format: (v) => fmtMonth(v) },
                { key: 'zip', label: `ZIP ${zip.postal_code || ''}`, align: 'right', format: (v) => (isNum(v) ? current.full(v) : DASH) },
                { key: 'peers', label: 'Peer ZIPs', align: 'right', format: (v) => (isNum(v) ? current.full(v) : DASH) },
                { key: 'county', label: 'County', align: 'right', format: (v) => (isNum(v) ? current.full(v) : DASH) },
              ],
              rows: tableRows,
            }}
            footnote={peers ? `Peer benchmark: ${peers.method ? peers.method.replace(/_/g, ' ') : 'median of nearby ZIPs'} · ${safeArray(peers.peer_postal_codes).length} ZIPs (subject excluded)` : null}
          >
            <LineChart
              series={series}
              yFormat={current.format}
              xFormat={(ms) => fmtMonth(new Date(ms).toISOString())}
              xTickFormat={(ms) => fmtMonthShort(new Date(ms).toISOString())}
              height={240}
              fromZero={metric !== 'median_listing_price_usd' && metric !== 'median_listing_price_per_sqft_usd'}
            />
          </ChartFrame>

          {notes.length ? <div className="le-footnotes">{notes.map((n) => <div key={n}>{n}</div>)}</div> : null}
          {status?.as_of ? <div className="le-footnotes muted">Data through {fmtMonth(status.as_of)} · market aggregates, not the subject property's own history</div> : null}
        </>
      ) : null}
    </SectionCard>
  );
}
